'use client'

import { useEffect } from 'react'
import { Button } from '@/ui/shared/components/button'
import { Card, CardContent } from '@/ui/shared/components/card'
import { AlertTriangle, RefreshCw, Home, Bug, Zap } from 'lucide-react'
import { browserAgent } from '@/lib/browser-agent'

export const dynamic = 'force-dynamic'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    browserAgent.recordError(error, {
      digest: error.digest,
      component: 'ErrorBoundary'
    })
  }, [error])

  return (
    <div className="min-h-screen bg-white">
      <section className="py-24 px-4 relative overflow-hidden min-h-screen flex items-center">
        <div className="absolute inset-0 w-full h-full">
          <div className="absolute top-10 sm:top-20 right-0 w-48 sm:w-64 lg:w-96 h-48 sm:h-64 lg:h-96 bg-gradient-to-br from-purple-200/30 to-pink-200/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 sm:bottom-20 left-10 sm:left-20 w-32 sm:w-48 lg:w-64 h-32 sm:h-48 lg:h-64 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-2xl"></div>
        </div>

        <div className="container mx-auto max-w-4xl relative z-10">
          <Card className="p-8 md:p-12 hover-lift bg-white border-gray-200 shadow-xl">
            <CardContent className="p-0 text-center">
              <div className="w-20 h-20 gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-8 glow-violet">
                <AlertTriangle className="w-10 h-10 text-white" />
              </div>

              <h1 className="onest-heading-main mb-6 text-gray-900 leading-tight">
                Упс! Что-то пошло не так
              </h1>

              <p className="onest-description text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                Произошла неожиданная ошибка. Наша команда уже уведомлена и работает над исправлением.
              </p>

              {process.env.NODE_ENV === 'development' && (
                <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left max-w-2xl mx-auto border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Bug className="w-5 h-5 text-purple-600" />
                    <span className="onest-feature-title text-purple-600">Детали ошибки (только в разработке):</span>
                  </div>
                  <pre className="onest-small text-gray-600 overflow-auto">
                    {error.message}
                    {error.digest && `\nDigest: ${error.digest}`}
                  </pre>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
                <Button
                  onClick={reset}
                  size="lg"
                  variant="primary"
                  className="text-white px-10 py-6 onest-button"
                >
                  <RefreshCw className="w-6 h-6 mr-3" />
                  Попробовать снова
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => window.location.replace('/')}
                  className="px-10 py-6 onest-button border-purple-300 hover:border-purple-600 hover:bg-purple-50 hover-lift bg-white"
                >
                  <Home className="w-6 h-6 mr-3" />
                  На главную
                </Button>
              </div>

              <div className="pt-8 border-t border-gray-200">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <Zap className="w-6 h-6 text-purple-600" />
                  <span className="onest-feature-title text-purple-600">Нужна помощь?</span>
                </div>
                <p className="onest-description text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                  Если проблема повторяется, свяжитесь с нашей службой поддержки. Мы здесь, чтобы помочь вам!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="outline"
                    asChild
                    className="border-purple-300 hover:border-purple-600 hover:bg-purple-50 hover-lift"
                  >
                    <a href="mailto:support@silqai.ru">
                      Написать на почту
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    asChild
                    className="border-purple-300 hover:border-purple-600 hover:bg-purple-50 hover-lift"
                  >
                    <a href="https://t.me/SilqAiHelp" target="_blank" rel="noopener noreferrer">
                      Написать в Telegram
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  )
}

